import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { Form, Input, Select, Button, message } from "antd";
import { INDUSTRY_OPTIONS } from "common/enums/Industry.enum";
import { CompanyInfoType } from "common/types/companyInfo.type";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS } from "config/apiConfig";
import { useCommonContext } from "contexts/CommonContext";

const CompanyManagementEditPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const { companyCode } = useParams();
  const [form] = Form.useForm();
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [companyData, setCompanyData] = useState<any>(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  const initialData: CompanyInfoType & {
    password?: string;
  } = {
    company_name: "",
    postal_code: "",
    prefecture: "",
    address: "",
    building_room: "",
    industry: "",
    contact_last_name: "",
    contact_first_name: "",
    phone_number: "",
    email: "",
    password: "",
  };

  useEffect(() => {
    if (!companyCode) return;
    fetchCompanyInfo();
  }, [companyCode]);

  const setDefaultValue = (defaultValue: any) => {
    if (!defaultValue) return;
    const formData: CompanyInfoType & { password?: string } = {
      company_name: defaultValue.company_name || "",
      postal_code: defaultValue.postal_code || "",
      prefecture: defaultValue.prefecture || "",
      address: defaultValue.address || "",
      building_room: defaultValue.building_room || "",
      industry: defaultValue.industry || "",
      contact_last_name: defaultValue.contact_last_name || "",
      contact_first_name: defaultValue.contact_first_name || "",
      phone_number: defaultValue.phone_number || "",
      email: defaultValue.email || "",
      password: defaultValue.password || "********",
    };
    form.setFieldsValue(formData);
    setErrors({});
  };

  const handleSave = async (values: any) => {
    if (!companyCode) return;
    setUpdateLoading(true);
    const payload = { ...values };
    if (payload.password === companyData.password) {
      delete values.password;
    }
    fetchUpdateCompanyInfo(values);
  };

  const handleCancel = () => {
    setDefaultValue(companyData);
    message.info("変更をキャンセルしました");
  };

  const fetchCompanyInfo = () => {
    axiosInstance
      .get(`${API_ENDPOINTS.COMPANY_INFO}/${companyCode}`)
      .then((res) => {
        setCompanyData(res.data?.data || null);
        setDefaultValue(res.data?.data || null);
      });
  };

  const fetchUpdateCompanyInfo = (data: any) => {
    axiosInstance
      .put(`${API_ENDPOINTS.COMPANY_INFO}/${companyCode}`, data)
      .then((res) => {
        setCompanyData(res.data?.data || null);
        setDefaultValue(res.data?.data || null);
        message.success("情報を更新しました");
      })
      .catch((err: any) => {
        message.error(err?.response?.data?.message || "更新に失敗しました");
      })
      .finally(() => {
        setUpdateLoading(false);
      });
  };

  useEffect(() => {
    setHeaderTitle("ユーザー管理");
  }, []);

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "ユーザー管理",
            link: "/admin/company-management",
          },
          {
            label: `ユーザー詳細（ID: #${companyCode}）`,
            link: companyCode
              ? `/admin/company-management/${companyCode}/view`
              : undefined,
          },
          {
            label: "ユーザー編集",
            isCurrent: true,
          },
        ]}
      />
      <Form
        form={form}
        initialValues={initialData}
        onFinish={handleSave}
        className="w-full max-w-[800px]"
      >
        <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-primary rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                  基本情報
                </div>
              </div>
            </div>
          </div>

          <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
            {/* 法人名 */}
            <div className="self-stretch md:inline-flex justify-start items-start gap-5">
              <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  法人名
                </div>
              </div>
              <div className="w-full md:w-auto flex-1 inline-flex flex-col justify-start items-start gap-[5px]">
                <Form.Item
                  name="company_name"
                  className="!mb-0 w-full"
                  rules={[
                    { required: true, message: "法人名を入力してください" },
                  ]}
                >
                  <Input
                    size="large"
                    status={errors.company_name ? "error" : undefined}
                    className="!h-12"
                  />
                </Form.Item>
              </div>
            </div>

            {/* 郵便番号 */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  郵便番号
                </div>
              </div>
              <div className="w-full md:w-auto flex justify-start items-center gap-3.5">
                <Form.Item
                  name="postal_code"
                  className="!mb-0"
                  rules={[
                    { required: true, message: "郵便番号を入力してください" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="123-4567"
                    status={errors.postal_code ? "error" : undefined}
                    className="!h-12 w-48"
                  />
                </Form.Item>
              </div>
            </div>

            {/* 都道府県 */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  都道府県
                </div>
              </div>
              <div className="w-full md:w-auto flex-1">
                <Form.Item
                  name="prefecture"
                  className="!mb-0"
                  rules={[
                    { required: true, message: "都道府県を入力してください" },
                  ]}
                >
                  <Input
                    size="large"
                    status={errors.prefecture ? "error" : undefined}
                    className="!h-12"
                  />
                </Form.Item>
              </div>
            </div>

            {/* 市区町村・番地 */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  市区町村・番地
                </div>
              </div>
              <div className="w-full md:w-auto flex-1">
                <Form.Item
                  name="address"
                  className="!mb-0"
                  rules={[
                    {
                      required: true,
                      message: "市区町村・番地を入力してください",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    status={errors.address ? "error" : undefined}
                    className="!h-12"
                  />
                </Form.Item>
              </div>
            </div>

            {/* 建物名・部屋番号 */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  建物名・部屋番号
                </div>
              </div>
              <div className="w-full md:w-auto flex-1">
                <Form.Item name="building_room" className="!mb-0">
                  <Input
                    size="large"
                    status={errors.building_room ? "error" : undefined}
                    className="!h-12"
                  />
                </Form.Item>
              </div>
            </div>

            {/* 業種 */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  業種
                </div>
              </div>
              <div className="w-full md:w-auto flex-1">
                <Form.Item
                  name="industry"
                  className="!mb-0"
                  rules={[
                    { required: true, message: "業種を選択してください" },
                  ]}
                >
                  <Select
                    size="large"
                    options={Object.values(INDUSTRY_OPTIONS)}
                    status={errors.industry ? "error" : undefined}
                    className="!h-12"
                    placeholder="業種を選択"
                  />
                </Form.Item>
              </div>
            </div>

            {/* 担当者名 */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  担当者名
                </div>
              </div>
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <Form.Item
                  name="contact_last_name"
                  className="!mb-0 flex-1"
                  rules={[{ required: true, message: "姓を入力してください" }]}
                >
                  <Input
                    size="large"
                    placeholder="姓"
                    status={errors.contact_last_name ? "error" : undefined}
                    className="!h-12"
                  />
                </Form.Item>
                <Form.Item
                  name="contact_first_name"
                  className="!mb-0 flex-1"
                  rules={[{ required: true, message: "名を入力してください" }]}
                >
                  <Input
                    size="large"
                    placeholder="名"
                    status={errors.contact_first_name ? "error" : undefined}
                    className="!h-12"
                  />
                </Form.Item>
              </div>
            </div>

            {/* 電話番号 */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  電話番号
                </div>
              </div>
              <div className="w-full md:w-auto flex-1">
                <Form.Item
                  name="phone_number"
                  className="!mb-0"
                  rules={[
                    { required: true, message: "電話番号を入力してください" },
                  ]}
                >
                  <Input
                    size="large"
                    status={errors.phone_number ? "error" : undefined}
                    className="!h-12"
                  />
                </Form.Item>
              </div>
            </div>

            {/* メールアドレス */}
            <div className="self-stretch md:inline-flex justify-start items-start gap-5">
              <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  メールアドレス
                </div>
              </div>
              <div className="w-full md:w-auto flex-1 inline-flex flex-col justify-start items-start gap-[5px]">
                <Form.Item
                  name="email"
                  className="!mb-0 w-full"
                  rules={[
                    {
                      required: true,
                      message: "メールアドレスを入力してください",
                    },
                  ]}
                >
                  <Input size="large" className="!h-12" />
                </Form.Item>
              </div>
            </div>

            {/* パスワード */}
            <div className="self-stretch md:inline-flex justify-start items-center gap-5">
              <div className="w-44 flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                  パスワード
                </div>
              </div>
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <Form.Item
                  name="password"
                  className="!mb-0 flex-1"
                  rules={[{ min: 8, message: "8文字以上で入力してください" }]}
                >
                  <Input.Password
                    size="large"
                    placeholder="新しいパスワード (任意)"
                    className="!h-12"
                    autoComplete="new-password"
                  />
                </Form.Item>
                {companyData?.password && (
                  <div className="text-xs text-gray-500 ml-2">
                    （現在のパスワードは暗号化されています）
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Form>

      {/* Action Buttons */}
      <div className="self-stretch flex flex-col justify-start items-start gap-[15px] md:gap-3.5 mt-6">
        <div className="self-stretch flex flex-col-reverse md:flex-row md:inline-flex justify-start items-start gap-[10px] md:gap-3.5">
          <Button
            type="default"
            size="large"
            onClick={handleCancel}
            className="w-80 min-h-14 px-5 py-2.5 bg-primary-light rounded-md !text-primary !font-bold !text-[16px] md:!text-lg hover:!bg-primary-light/80"
          >
            変更をキャンセル
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={() => form.submit()}
            className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md !text-white !font-bold !text-[16px] md:!text-lg hover:!bg-primary/90"
            loading={updateLoading}
          >
            情報を保存する
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyManagementEditPage;
